import { IconPencil } from '@tabler/icons-react';
import type { StyledMantineProps } from '@dealstack/frontend/app/pages/Project/Cap/DealstackAi/theme/styledMantine.types';
import styled from 'styled-components';
import { useState } from 'react';
import { Box, Group, Skeleton, Stack, Text } from '@mantine/core';
import { DsaiIngestionProgressStatus } from '@dealstack/shared/lib/generated';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import {
	DS_AI_MOTION,
	DS_AI_TABULAR_FIGURES,
	dsAiMoveMs,
} from '@dealstack/frontend/app/pages/Project/Cap/DealstackAi/theme/dealstackAi.tokens';
import { fadeIn } from '@dealstack/frontend/app/pages/Project/Cap/DealstackAi/theme/dealstackAi.animations';
import { PROCESSING_CARD_VIEW_TRANSITION_NAME } from '../../viewTransition.constants';
import type {
	ProcessingBatchTitleField,
	ProcessingCardActions,
	ProcessingCardContent,
} from './ProcessingCard.types';
import { activeStageKey } from './ProcessingCard.utils';
import { WorkspaceIconButton } from '../../AnalysisWorkspace/components/primitives/WorkspaceIconButton';
import { ProcessingFooterActions } from './ProcessingFooterActions';
import { ProcessingReaderStatus } from './ProcessingReaderStatus';
import { ProcessingSettledFooter } from './ProcessingSettledFooter';
import { ProcessingStageItem } from './ProcessingStageItem';

interface Props extends ProcessingCardContent {
	batchTitle: ProcessingBatchTitleField;
	actions: ProcessingCardActions;
}

/** The server's pipeline stages as a checklist; the active stage shows its details inline, a done one in a popover. */
export const ProcessingCard = ({
	phase,
	failedStage,
	headingKey,
	subheading,
	facts,
	stages,
	progress,
	isLoading,
	readerStatus,
	noticeKey,
	batchTitle,
	actions,
}: Props) => {
	const tCapManage = useTranslationCapManage();
	const activeKey = failedStage ?? activeStageKey(stages);
	const [renaming, setRenaming] = useState(false);
	const stepNumber = stages.findIndex((stage) => stage.key === activeKey) + 1;
	const showStep = phase === DsaiIngestionProgressStatus.PROCESSING && stepNumber > 0;
	const finishRename = () => {
		batchTitle.onBlur();
		setRenaming(false);
	};

	return (
		<Stack
			component="section"
			align="center"
			gap="sm"
			w="100%"
			maw="34.75rem"
			style={{ viewTransitionName: PROCESSING_CARD_VIEW_TRANSITION_NAME }}
			data-testid={TEST_ELEMENT_IDS.DEALSTACK_AI_INGEST_PROCESSING_CARD}
			data-phase={phase}
		>
			<Stack
				component="header"
				key={headingKey}
				align="center"
				gap="xs"
				ta="center"
				aria-live="polite"
				styles={{
					root: {
						animation: `${fadeIn} ${DS_AI_MOTION.baseMs}ms ${DS_AI_MOTION.easing} both`,
					},
				}}
			>
				{renaming ? (
					<RenameField
						component="input"
						display="block"
						h="2.5rem"
						mx="auto"
						px={12}
						ta="center"
						c="dark.8"
						bg="white.0"
						bd="1px solid blue.6"
						fz="1.5rem"
						fw={600}
						lh="2rem"
						data-testid={TEST_ELEMENT_IDS.DEALSTACK_AI_INGEST_BATCH_TITLE_INPUT}
						aria-label={tCapManage('dealstack_ai.ingest.processing.rename_label')}
						placeholder={tCapManage(
							'dealstack_ai.ingest.processing.rename_placeholder'
						)}
						value={batchTitle.value}
						onChange={(event) => batchTitle.onChange(event.currentTarget.value)}
						onBlur={finishRename}
						onKeyDown={(event) => {
							if (event.key === 'Enter' || event.key === 'Escape') {
								event.currentTarget.blur();
							}
						}}
						autoFocus
					/>
				) : (
					<Group justify="center" gap={6} miw={0} wrap="nowrap">
						<Text
							component="h2"
							m={0}
							fz="1.5rem"
							fw={600}
							lh="2rem"
							c="dark.8"
							tabIndex={-1}
							data-autofocus
							styles={{ root: { outline: 'none' } }}
							style={{ minWidth: 0, overflowWrap: 'anywhere' }}
						>
							{batchTitle.value || tCapManage('dealstack_ai.batch_list.untitled')}
						</Text>
						<WorkspaceIconButton
							tone="muted"
							size={28}
							label={tCapManage('dealstack_ai.ingest.processing.rename_action')}
							onClick={() => setRenaming(true)}
						>
							<IconPencil size={16} stroke={1.5} />
						</WorkspaceIconButton>
					</Group>
				)}
				<Text
					component="p"
					fz="sm"
					c="dark.0"
					m={0}
					data-testid={TEST_ELEMENT_IDS.DEALSTACK_AI_INGEST_PROCESSING_SUBHEADING}
				>
					{tCapManage(headingKey)}
					{showStep && (
						<>
							<Text span mx="xs" c="gray.8" aria-hidden>
								·
							</Text>
							{tCapManage('dealstack_ai.ingest.processing.step_of', {
								current: stepNumber,
								total: stages.length,
							})}
						</>
					)}
					{subheading && (
						<>
							<Text span mx="xs" c="gray.8" aria-hidden>
								·
							</Text>
							{tCapManage(subheading.lead.key, subheading.lead.values)}
							{subheading.emphasis && (
								<Text component="strong" inherit ml={4} c="dark.8" fw={600}>
									{tCapManage(
										subheading.emphasis.key,
										subheading.emphasis.values
									)}
								</Text>
							)}
						</>
					)}
				</Text>
			</Stack>
			<Box
				w="100%"
				bg="white.0"
				bd="1px solid gray.4"
				style={{ borderRadius: '4px', overflow: 'hidden' }}
			>
				<Box
					h="3px"
					bg="gray.2"
					role="progressbar"
					aria-valuemin={0}
					aria-valuemax={100}
					aria-valuenow={Math.round(progress * 100)}
				>
					<ProgressFill
						h="100%"
						w="100%"
						bg="blue.6"
						style={{
							transformOrigin: 'left center',
							transform: `scaleX(${progress})`,
						}}
					/>
				</Box>
				{facts.length > 0 && (
					<FadeInBlock
						component="dl"
						display="grid"
						m={0}
						py={12}
						px="sm"
						style={{
							gridTemplateColumns: 'repeat(auto-fit, minmax(8rem, 1fr))',
							gap: '16px',
							borderTop: '1px solid var(--mantine-color-gray-4)',
						}}
					>
						{facts.map((fact) => (
							<Stack
								key={fact.key}
								gap={2}
								miw={0}
								data-testid={TEST_ELEMENT_IDS.DEALSTACK_AI_INGEST_PROCESSING_FACT}
							>
								<Text component="dt" fz="sm" c="dark.0">
									{tCapManage(fact.labelKey)}
								</Text>
								<Text
									component="dd"
									size="md"
									fw={600}
									c="dark.8"
									m={0}
									styles={{ root: DS_AI_TABULAR_FIGURES }}
								>
									{tCapManage(fact.value.key, fact.value.values)}
								</Text>
							</Stack>
						))}
					</FadeInBlock>
				)}
				<Stack
					gap={0}
					pt={10}
					px={4}
					pb={12}
					style={{ borderTop: '1px solid var(--mantine-color-gray-4)' }}
				>
					{isLoading ? (
						<Stack gap="xs" align="stretch" justify="flex-start">
							<Skeleton height={20} width="40%" />
							<Skeleton height={20} width="75%" />
							<Skeleton height={20} width="60%" />
						</Stack>
					) : (
						stages.map((stage) => (
							<ProcessingStageItem
								key={stage.key}
								stage={stage}
								expanded={stage.key === activeKey}
								failed={stage.key === failedStage}
							/>
						))
					)}
					{readerStatus && <ProcessingReaderStatus status={readerStatus} />}
					{noticeKey && (
						<Text component="p" fz="sm" c="dark.0" m={0}>
							{tCapManage(noticeKey)}
						</Text>
					)}
				</Stack>
				{phase === DsaiIngestionProgressStatus.PROCESSING ? (
					<ProcessingFooterActions actions={actions} />
				) : (
					<ProcessingSettledFooter phase={phase} actions={actions} />
				)}
			</Box>
		</Stack>
	);
};

/** The inline rename field for a processing batch. */
const RenameField = styled(Box)<StyledMantineProps<'input'>>({
	width: 'min(28.75rem, 100%)',
	borderRadius: 4,
	font: 'inherit',
	outline: 'none',
});

/** The processing progress fill. */
const ProgressFill = styled(Box)<StyledMantineProps>({
	transition: `transform ${dsAiMoveMs(DS_AI_MOTION.barGrowMs)} ${DS_AI_MOTION.easing}`,
});

/** Content that fades in once its stage reports. */
const FadeInBlock = styled(Box)<StyledMantineProps<'dl'>>({
	animation: `${fadeIn} ${DS_AI_MOTION.baseMs}ms ${DS_AI_MOTION.easing} both`,
});
